(() => {
  if (window.__kivioraF05CheckoutGateBound) return;
  window.__kivioraF05CheckoutGateBound = true;

  const consentSelector = '[data-kiviora-f05-consent]';
  const containerSelector = '[data-kiviora-f05-consent-container]';
  const checkoutSelector = '[data-kiviora-f05-standard-checkout]';
  const gateSelector = '[data-kiviora-f05-accelerated-gate]';
  const walletSelector = '[data-kiviora-f05-accelerated-checkout]';
  const termsSelector = '[data-tncbox="tncBox"], [data-tncbox="gmtncBox"]';

  const consentAttribute = 'Digital content consent';
  const termsCartAttribute = 'tnc_term_and_condition';

  let state = 'pending';
  let operation = 0;
  let mountedContainer = null;
  let termsPersisted = false;
  let knownCartScope = null;
  let renderQueued = false;
  let termsSyncTimer = null;
  let cartSyncTimer = null;

  const containers = () =>
    [...document.querySelectorAll(containerSelector)];

  const currentConfig = () => {
    const container = containers()[0];

    if (!container) return null;

    return {
      consentValue: container.dataset.kivioraF05ConsentValue || '',
      scopeAttribute: container.dataset.kivioraF05ScopeAttribute || '',
    };
  };

  const closest = (target, selector) =>
    target instanceof Element ? target.closest(selector) : null;

  const currentConsent = () =>
    document.querySelector(consentSelector);

  const termsControls = () =>
    [...document.querySelectorAll(termsSelector)].filter(
      (control) =>
        control instanceof HTMLInputElement &&
        control.type === 'checkbox' &&
        !control.disabled
    );

  const termsCheckedInDom = () =>
    termsControls().some((control) => control.checked);

  const isConfirmed = () =>
    state === 'confirmed';

  const checkoutEligible = () =>
    isConfirmed() &&
    termsCheckedInDom() &&
    termsPersisted;

  const cartUrl = () => {
    const route = window.Theme?.routes?.cart_url || '/cart';

    return route.endsWith('.js')
      ? route
      : `${route}.js`;
  };

  const cartUpdateUrl = () => {
    const route =
      window.Theme?.routes?.cart_update_url || '/cart/update';

    return route.endsWith('.js')
      ? route
      : `${route}.js`;
  };

  const getCart = async () => {
    const response = await fetch(cartUrl(), {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Unable to read the current cart.');
    }

    return response.json();
  };

  const updateAttributes = async (attributes) => {
    const response = await fetch(cartUpdateUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ attributes }),
    });

    if (!response.ok) {
      throw new Error('Unable to update the current cart.');
    }

    return response.json();
  };

  const cartScope = (cart) => {
    const lines = (cart.items || [])
      .map((item) => `${item.key}:${item.quantity}`)
      .sort()
      .join('|');

    let hash = 2166136261;

    for (let index = 0; index < lines.length; index += 1) {
      hash ^= lines.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }

    return `f05-v1-${(hash >>> 0).toString(16)}`;
  };

  const consentMatches = (cart, config) =>
    Boolean(
      config &&
        config.consentValue &&
        config.scopeAttribute &&
        cart?.attributes?.[consentAttribute] === config.consentValue &&
        cart.attributes[config.scopeAttribute] === cartScope(cart)
    );

  const termsMatches = (cart) =>
    cart?.attributes?.[termsCartAttribute] === 'cart';

  const showError = () => {
    document
      .querySelectorAll('#kiviora-digital-content-consent-error')
      .forEach((error) => {
        error.hidden = false;
      });
  };

  const clearError = () => {
    document
      .querySelectorAll('#kiviora-digital-content-consent-error')
      .forEach((error) => {
        error.hidden = true;
      });
  };

  const setCheckoutDisabled = (blocked) => {
    document.querySelectorAll(checkoutSelector).forEach((control) => {
      if (
        !(control instanceof HTMLButtonElement) &&
        !(control instanceof HTMLInputElement)
      ) {
        return;
      }

      if (
        blocked ||
        control.dataset.kivioraF05CartEmpty === 'true'
      ) {
        control.disabled = true;
        control.setAttribute('aria-disabled', 'true');
        return;
      }

      control.disabled = false;
      control.removeAttribute('aria-disabled');
    });
  };

  const render = (nextState = state) => {
    state = nextState;

    const confirmed = isConfirmed();
    const eligible = checkoutEligible();

    containers().forEach((container) => {
      container.dataset.kivioraF05State = nextState;
      container.setAttribute(
        'aria-busy',
        nextState === 'pending' ? 'true' : 'false'
      );

      const consent = container.querySelector(consentSelector);

      if (consent instanceof HTMLInputElement) {
        if (nextState === 'confirmed') {
          consent.checked = true;
        }

        if (nextState === 'off') {
          consent.checked = false;
        }

        consent.disabled = nextState === 'pending';
      }
    });

    setCheckoutDisabled(!eligible);

    document.querySelectorAll(walletSelector).forEach((wallet) => {
      wallet.dataset.kivioraF05State =
        eligible ? 'confirmed' : 'blocked';

      wallet.setAttribute(
        'aria-disabled',
        eligible ? 'false' : 'true'
      );
    });

    document.querySelectorAll(gateSelector).forEach((gate) => {
      gate.hidden = eligible;
      gate.tabIndex = eligible ? -1 : 0;
    });

    if (confirmed) {
      clearError();
    }
  };

  const scheduleRender = () => {
    if (renderQueued) return;

    renderQueued = true;

    requestAnimationFrame(() => {
      renderQueued = false;

      if (document.querySelector(containerSelector)) {
        render(state);
      }
    });
  };

  const clearPersistedConsent = async () => {
    const config = currentConfig();

    if (!config?.scopeAttribute) return null;

    return updateAttributes({
      [consentAttribute]: '',
      [config.scopeAttribute]: '',
    });
  };

  const writeConsent = async (cart, config) =>
    updateAttributes({
      [consentAttribute]: config.consentValue,
      [config.scopeAttribute]: cartScope(cart),
    });

  const readStableCart = async (
    predicate,
    attempts = 12,
    interval = 150
  ) => {
    let cart = null;

    for (let attempt = 0; attempt < attempts; attempt += 1) {
      cart = await getCart();

      if (!predicate || predicate(cart)) {
        return cart;
      }

      await new Promise((resolve) => {
        window.setTimeout(resolve, interval);
      });
    }

    return cart;
  };

  const invalidateConsent = async () => {
    const token = ++operation;

    render('pending');

    try {
      await clearPersistedConsent();

      if (token !== operation) return;

      const cart = await getCart();

      if (token !== operation) return;

      knownCartScope = cartScope(cart);
      termsPersisted = termsMatches(cart);

      render('off');
    } catch {
      if (token === operation) {
        termsPersisted = false;
        render('off');
      }
    }
  };

  const syncFromCart = async () => {
    const config = currentConfig();

    if (!config) return;

    const token = ++operation;

    render('pending');

    try {
      const cart = await getCart();

      if (token !== operation) return;

      knownCartScope = cartScope(cart);
      termsPersisted = termsMatches(cart);

      if (consentMatches(cart, config)) {
        render('confirmed');
        return;
      }

      if (
        cart?.attributes?.[consentAttribute] ||
        cart?.attributes?.[config.scopeAttribute]
      ) {
        await clearPersistedConsent();

        if (token !== operation) return;
      }

      render('off');
    } catch {
      if (token === operation) {
        termsPersisted = false;
        render('off');
      }
    }
  };

  const persistConsent = async () => {
    const config = currentConfig();

    if (!config?.consentValue || !config.scopeAttribute) {
      render('off');
      return;
    }

    const token = ++operation;

    render('pending');

    try {
      let cart = await getCart();

      if (token !== operation) return;

      knownCartScope = cartScope(cart);

      await writeConsent(cart, config);

      if (token !== operation) return;

      cart = await readStableCart(
        (currentCart) =>
          consentMatches(currentCart, config),
        8,
        125
      );

      if (token !== operation) return;

      termsPersisted = termsMatches(cart);

      if (consentMatches(cart, config)) {
        render('confirmed');
        return;
      }

      render('off');
    } catch {
      if (token === operation) {
        render('off');
      }
    }
  };

  const reconcileAfterTerms = async () => {
    const config = currentConfig();

    if (!config) return;

    const wantsF05 =
      isConfirmed() ||
      Boolean(currentConsent()?.checked);

    const wantsTerms = termsCheckedInDom();

    const token = ++operation;

    render('pending');

    try {
      let cart;

      if (wantsTerms) {
        cart = await readStableCart(
          (currentCart) => termsMatches(currentCart),
          15,
          150
        );
      } else {
        cart = await getCart();
      }

      if (token !== operation) return;

      knownCartScope = cartScope(cart);
      termsPersisted = termsMatches(cart);

      if (!wantsTerms || !termsPersisted) {
        render(wantsF05 ? 'confirmed' : 'off');
        return;
      }

      if (!wantsF05) {
        render('off');
        return;
      }

      if (!consentMatches(cart, config)) {
        await writeConsent(cart, config);

        if (token !== operation) return;
      }

      const verifiedCart = await readStableCart(
        (currentCart) =>
          termsMatches(currentCart) &&
          consentMatches(currentCart, config),
        12,
        150
      );

      if (token !== operation) return;

      knownCartScope = cartScope(verifiedCart);
      termsPersisted = termsMatches(verifiedCart);

      if (
        termsPersisted &&
        consentMatches(verifiedCart, config)
      ) {
        render('confirmed');
        return;
      }

      render('off');
    } catch {
      if (token === operation) {
        render('off');
      }
    }
  };

  const reconcileCartEvent = async () => {
    const config = currentConfig();

    if (!config) return;

    try {
      const cart = await getCart();
      const nextScope = cartScope(cart);

      if (
        knownCartScope !== null &&
        nextScope !== knownCartScope
      ) {
        await invalidateConsent();
        return;
      }

      knownCartScope = nextScope;
      termsPersisted = termsMatches(cart);

      if (consentMatches(cart, config)) {
        render('confirmed');
        return;
      }

      if (currentConsent()?.checked) {
        await persistConsent();
        return;
      }

      render('off');
    } catch {
      scheduleRender();
    }
  };

  const scheduleTermsReconciliation = () => {
    if (termsSyncTimer) {
      clearTimeout(termsSyncTimer);
    }

    termsSyncTimer = setTimeout(() => {
      termsSyncTimer = null;
      reconcileAfterTerms();
    }, 100);
  };

  const scheduleCartReconciliation = () => {
    if (cartSyncTimer) {
      clearTimeout(cartSyncTimer);
    }

    cartSyncTimer = setTimeout(() => {
      cartSyncTimer = null;
      reconcileCartEvent();
    }, 100);
  };

  const focusConsent = () => {
    const consent = currentConsent();

    if (consent instanceof HTMLElement) {
      consent.focus({ preventScroll: false });
    }
  };

  const focusTerms = () => {
    const terms = termsControls()[0];

    if (terms instanceof HTMLElement) {
      terms.focus({ preventScroll: false });
    }
  };

  const blockCheckout = (event) => {
    if (checkoutEligible()) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (!isConfirmed()) {
      showError();
      focusConsent();
      return;
    }

    focusTerms();
  };

  const acceleratedCheckoutTags = new Set([
    'SHOP-PAY-WALLET-BUTTON',
    'SHOPIFY-AMAZON-PAY-BUTTON',
    'SHOPIFY-APPLE-PAY-BUTTON',
    'SHOPIFY-GOOGLE-PAY-BUTTON',
    'SHOPIFY-PAYPAL-BUTTON',
  ]);

  const isAcceleratedCheckoutEvent = (event) =>
    event.composedPath().some(
      (node) =>
        node instanceof Element &&
        (
          node.matches?.(gateSelector) ||
          node.matches?.(walletSelector) ||
          acceleratedCheckoutTags.has(node.tagName)
        )
    );

  const isCartMutationControl = (target) =>
    Boolean(
      closest(
        target,
        'form[action*="/cart/add"], [data-testid="standalone-add-to-cart"], cart-items button, .cart-items button, quantity-selector button, .quantity-selector button, [data-cart-item-remove]'
      )
    );

  const mount = () => {
    const container = document.querySelector(containerSelector);

    if (!container || container === mountedContainer) return;

    mountedContainer = container;
    syncFromCart();
  };

  document.addEventListener(
    'click',
    (event) => {
      if (
        isAcceleratedCheckoutEvent(event) &&
        !checkoutEligible()
      ) {
        blockCheckout(event);
        return;
      }

      if (
        closest(event.target, checkoutSelector) &&
        !checkoutEligible()
      ) {
        blockCheckout(event);
        return;
      }

      if (isCartMutationControl(event.target)) {
        scheduleCartReconciliation();
      }
    },
    true
  );

  document.addEventListener(
    'pointerdown',
    (event) => {
      if (
        isAcceleratedCheckoutEvent(event) &&
        !checkoutEligible()
      ) {
        blockCheckout(event);
      }
    },
    true
  );

  document.addEventListener(
    'keydown',
    (event) => {
      if (
        (event.key === 'Enter' || event.key === ' ') &&
        isAcceleratedCheckoutEvent(event) &&
        !checkoutEligible()
      ) {
        blockCheckout(event);
      }
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;

      if (!(form instanceof HTMLFormElement)) return;

      if (
        form.id === 'cart-form' &&
        !checkoutEligible()
      ) {
        blockCheckout(event);
        return;
      }

      if (form.action.includes('/cart/add')) {
        scheduleCartReconciliation();
      }
    },
    true
  );

  document.addEventListener(
    'change',
    (event) => {
      const consent = closest(event.target, consentSelector);

      if (consent instanceof HTMLInputElement) {
        if (consent.checked) {
          persistConsent();
        } else {
          invalidateConsent();
        }

        return;
      }

      if (closest(event.target, termsSelector)) {
        scheduleTermsReconciliation();
        return;
      }

      if (
        closest(
          event.target,
          'cart-items input, .cart-items input, quantity-selector input, .quantity-selector input'
        )
      ) {
        scheduleCartReconciliation();
      }
    },
    true
  );

  ['cart:change', 'cart:updated', 'cart:refresh'].forEach(
    (eventName) => {
      document.addEventListener(
        eventName,
        scheduleCartReconciliation
      );
    }
  );

  new MutationObserver(() => {
    const container = document.querySelector(containerSelector);

    if (container && container !== mountedContainer) {
      mountedContainer = container;
      syncFromCart();
      return;
    }

    scheduleRender();
  }).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  mount();
})();